import { Link } from "react-router";
import { useTheme } from "@/components/ThemeProvider";
import { useLocation } from "react-router";
import { useEffect, useRef, useState } from "react";
import type { UserRole } from "@/lib/auth/session";
import { activeNavItem, visibleNavItems, type NavItem } from "./nav";
import { PendingInvitesBanner } from "@/components/PendingInvitesBanner";
import { ProlyMark } from "@/components/ProlyMark";

type ShellUser = {
  name: string | null;
  email: string | null;
  image: string | null;
};

type AppShellProps = {
  user: ShellUser;
  role: UserRole;
  onSignOut: () => void;
  children: React.ReactNode;
};

export function AppShell({ user, role, onSignOut, children }: AppShellProps) {
  const { pathname } = useLocation();
  const items = visibleNavItems(role);
  const active = activeNavItem(pathname, role);
  const activeHref = active?.href;

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Close user menu on outside click / escape.
  useEffect(() => {
    if (!userMenuOpen) return;
    function onClick(e: MouseEvent) {
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setUserMenuOpen(false);
    }
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [userMenuOpen]);

  // Close mobile nav on route change.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMobileNavOpen(false);
  }, [pathname]);

  const initial = (user.name ?? user.email ?? "?").slice(0, 1).toUpperCase();
  const roleLabel = role === "SUPER_ADMIN" ? "Super admin" : "Member";

  return (
    <div className="flex min-h-screen flex-col bg-[color:var(--background)] text-[color:var(--foreground)]">
      {/* TOP NAVBAR */}
      <header className="sticky top-0 z-30 h-12 border-b border-zinc-200 dark:border-border bg-white/80 dark:bg-surface/85 backdrop-blur supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-surface/65">
        <div className="flex h-full items-center gap-3 px-3 lg:px-4">
          {/* Mobile hamburger (lg- only) */}
          <button
            type="button"
            aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileNavOpen}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-zinc-200 dark:border-border text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-surface-2 lg:hidden"
            onClick={() => setMobileNavOpen((v) => !v)}
          >
            <Icon name={mobileNavOpen ? "x" : "menu"} />
          </button>

          {/* Brand */}
          <Link
            to="/"
            className="flex items-center gap-2 pr-2"
            aria-label="Proly home"
          >
            <ProlyMark size={28} radius={8} />
            <span className="brand-wordmark hidden text-[15px] font-semibold text-zinc-900 dark:text-zinc-100 sm:inline">
              Proly
            </span>
          </Link>

          <div className="ml-1 hidden h-5 w-px bg-zinc-200 dark:bg-surface-2 lg:block" />

          {/* Inline nav (md+) */}
          <nav
            className="ml-1 hidden flex-1 items-center gap-0.5 md:flex"
            aria-label="Primary"
          >
            {items.map((item) => {
              const isActive = item.href === activeHref;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  aria-current={isActive ? "page" : undefined}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] transition",
                    isActive
                      ? "font-semibold text-zinc-900 dark:text-zinc-100 bg-zinc-100 dark:bg-surface-2"
                      : "font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-surface-2 hover:text-zinc-900 dark:hover:text-zinc-100",
                  ].join(" ")}
                >
                  <span className="text-zinc-500 dark:text-zinc-400">
                    <Icon name={item.icon} />
                  </span>
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Spacer for md- screens to push user menu right */}
          <div className="flex-1 md:hidden" />

          <ThemeToggleButton />

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              aria-label="Open account menu"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              onClick={() => setUserMenuOpen((v) => !v)}
              className="inline-flex items-center gap-2 rounded-md py-1 pl-1 pr-2 hover:bg-zinc-100 dark:hover:bg-surface-2"
            >
              {user.image ? (
                <img
                  src={user.image}
                  alt=""
                  className="h-7 w-7 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 text-[11px] font-semibold text-white">
                  {initial}
                </span>
              )}
              <span className="hidden text-[13px] font-medium text-zinc-700 dark:text-zinc-300 sm:inline">
                {user.name ?? user.email ?? "Account"}
              </span>
              <span className="text-zinc-400 dark:text-zinc-500">
                <Icon name="chevron-down" />
              </span>
            </button>

            {userMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-[calc(100%+6px)] z-40 w-64 overflow-hidden rounded-[10px] border border-zinc-200 dark:border-border bg-white dark:bg-surface shadow-[0_8px_24px_-8px_rgb(0_0_0/0.12)]"
              >
                <div className="border-b border-zinc-100 dark:border-border px-3 py-2.5">
                  <p className="truncate text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
                    {user.name ?? "Signed in"}
                  </p>
                  <p className="truncate text-[12px] text-zinc-500 dark:text-zinc-400">
                    {user.email ?? "No email"}
                  </p>
                  <p className="mt-1 inline-flex items-center rounded-full bg-zinc-100 dark:bg-surface-2 px-2 py-0.5 text-[11px] font-medium text-zinc-600 dark:text-zinc-400">
                    {roleLabel}
                  </p>
                </div>
                <button
                    type="button"
                    onClick={onSignOut}
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] font-medium text-zinc-700 dark:text-zinc-300 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 dark:hover:text-red-300"
                  >
                    <Icon name="signout" />
                    <span>Sign out</span>
                  </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile slide-down nav */}
        {mobileNavOpen && (
          <div className="border-t border-zinc-200 dark:border-border bg-white dark:bg-surface md:hidden">
            <nav className="flex flex-col px-2 py-2" aria-label="Mobile">
              {items.map((item) => {
                const isActive = item.href === activeHref;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => setMobileNavOpen(false)}
                    className={[
                      "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[14px]",
                      isActive
                        ? "bg-zinc-100 dark:bg-surface-2 font-semibold text-zinc-900 dark:text-zinc-100"
                        : "font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-surface-2",
                    ].join(" ")}
                  >
                    <span className="text-zinc-500 dark:text-zinc-400">
                      <Icon name={item.icon} />
                    </span>
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* Pending workspace invitations banner */}
      <PendingInvitesBanner />

      {/* BODY */}
      <main className="min-h-0 flex-1">{children}</main>
    </div>
  );
}

function ThemeToggleButton() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const className =
    "inline-flex h-8 w-8 items-center justify-center rounded-md border border-zinc-200 text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900 dark:border-border dark:text-zinc-400 dark:hover:bg-surface-2 dark:hover:text-zinc-100";

  if (!mounted) {
    return <span aria-hidden="true" className={`${className} pointer-events-none`} />;
  }

  const isDark = resolvedTheme === "dark";

  return (
    <button
      type="button"
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      title={isDark ? "Switch to light theme" : "Switch to dark theme"}
      className={className}
      onClick={() => setTheme(isDark ? "light" : "dark")}
    >
      <Icon name={isDark ? "sun" : "moon"} />
    </button>
  );
}

type IconName =
  | NavItem["icon"]
  | "menu"
  | "moon"
  | "signout"
  | "sun"
  | "x"
  | "sidebar"
  | "chevron-down"
  | "chevron-left"
  | "chevron-right";

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, React.ReactNode> = {
    dashboard: (
      <>
        <rect x="3" y="3" width="7" height="9" rx="1" />
        <rect x="14" y="3" width="7" height="5" rx="1" />
        <rect x="14" y="12" width="7" height="9" rx="1" />
        <rect x="3" y="16" width="7" height="5" rx="1" />
      </>
    ),
    upload: (
      <>
        <path d="M12 16V4" />
        <path d="m7 9 5-5 5 5" />
        <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
      </>
    ),
    workbooks: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18" />
        <path d="M9 21V9" />
      </>
    ),
    profiles: (
      <>
        <rect x="8" y="3" width="8" height="3" rx="1" />
        <path d="M16 4h2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        <path d="M9 12h6" />
        <path d="M9 16h4" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
      </>
    ),
    users: (
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
    menu: (
      <>
        <path d="M3 6h18" />
        <path d="M3 12h18" />
        <path d="M3 18h18" />
      </>
    ),
    moon: (
      <>
        <path d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5 7 7 0 1 0 20.5 14.5Z" />
      </>
    ),
    sun: (
      <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2" />
        <path d="M12 20v2" />
        <path d="m4.93 4.93 1.41 1.41" />
        <path d="m17.66 17.66 1.41 1.41" />
        <path d="M2 12h2" />
        <path d="M20 12h2" />
        <path d="m6.34 17.66-1.41 1.41" />
        <path d="m19.07 4.93-1.41 1.41" />
      </>
    ),
    x: (
      <>
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </>
    ),
    sidebar: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M9 4v16" />
      </>
    ),
    "chevron-down": (
      <>
        <path d="m6 9 6 6 6-6" />
      </>
    ),
    "chevron-left": (
      <>
        <path d="m15 18-6-6 6-6" />
      </>
    ),
    "chevron-right": (
      <>
        <path d="m9 18 6-6-6-6" />
      </>
    ),
    signout: (
      <>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="m16 17 5-5-5-5" />
        <path d="M21 12H9" />
      </>
    ),
  };

  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      {paths[name]}
    </svg>
  );
}
